import { FC, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useOptionalAuthorizationService } from 'src/apis/authorization-service/use-authorization-service';
import { HeaderPrimary } from 'src/components/layout/header/primary';
import { HeaderSecondary } from 'src/components/layout/header/secondary';
import { isAggregateEuActive } from 'src/components/layout/header/secondary-nav-aggregate-eu';
import { isDashboardActive } from 'src/components/layout/header/secondary-nav-dashboard';
import { isGdmActive } from 'src/components/layout/header/secondary-nav-gdm';
import { isLngActive } from 'src/components/layout/header/secondary-nav-lng';
import { isReportingActive } from 'src/components/layout/header/secondary-nav-reporting';
import { isStorageActive } from 'src/components/layout/header/secondary-nav-storage';
import { isTransportActive } from 'src/components/layout/header/secondary-nav-transport';
import { useMobileNav } from 'src/components/layout/header/use-mobile-nav';
import { LayoutHeader } from 'src/components/layout/layout';
import { SimpleHeader } from 'src/components/layout/simple-page-layout';
import { AngularModalDimmer } from 'src/components/overlay/angular-modal-dimmer';
import { stickyHeaderId } from 'src/components/scroll-management';
import { Successful } from 'src/hooks/use-axios';
import { useHeaderHeightMeasure } from 'src/hooks/use-header-height';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import {
  OrganisationServiceRequest,
  useOptionalOrganisationService,
} from 'src/pages/settings/organisation/use-organisation-service';
import { useIsSnamAdmin } from 'src/pages/snam-bid-collector/use-is-snam-admin';
import { Colors } from 'src/styles';
import styled from 'styled-components';

export const Header: FC = () => {
  const { pathname, hash } = useLocation();
  const monolithUser = useOptionalAuthenticatedMonolithUser();

  const activeSection = useMemo(() => {
    const dashboard = isDashboardActive(pathname, hash, monolithUser?.role);
    const transport = isTransportActive(pathname, hash);
    const storage = isStorageActive(pathname, hash, monolithUser);
    const lng = isLngActive(pathname, hash, monolithUser);
    const reporting = isReportingActive(pathname, hash);
    const gdm = isGdmActive(pathname);
    const aggregateEu = isAggregateEuActive(pathname);
    return { dashboard, transport, storage, reporting, gdm, lng, aggregateEu };
  }, [pathname, hash, monolithUser]);

  const authorizationService = useOptionalAuthorizationService();
  const organisationService = useOptionalOrganisationService();

  const ref = useHeaderHeightMeasure();
  const isSnamAdmin = useIsSnamAdmin();
  const mobileNav = useMobileNav();

  if (
    (authorizationService?.response === null &&
      authorizationService.error === null) ||
    (organisationService.response === null &&
      organisationService.error === null) ||
    isSnamAdmin === undefined
  )
    return (
      <LayoutHeader id={stickyHeaderId} ref={ref}>
        <SimpleHeader />
      </LayoutHeader>
    );

  return (
    <LayoutHeader
      id={stickyHeaderId}
      ref={ref}
      fullscreen={mobileNav.value !== null}
      onClick={(e) => {
        if (mobileNav.value === null) return;
        // when the mobile nav is open the LayoutHeader is covering the whole screen,
        // even if it is invisble. when the user directly clicks on the LayoutHeader (which means the
        // user actually wanted to click _outside_ the mobile nav) we close the mobile nav.
        if (e.target instanceof HTMLElement && e.target.id === stickyHeaderId)
          mobileNav.setValue(null);
      }}
    >
      <AngularModalDimmer>
        <header>
          <HeaderPrimary
            activeSection={activeSection}
            authorizationService={
              authorizationService?.response ? authorizationService : undefined
            }
            organisationService={
              organisationService.response === null
                ? undefined
                : (organisationService as Successful<OrganisationServiceRequest>)
            }
            mobileNav={mobileNav}
          />
          <HeaderSecondary
            isSnamAdmin={isSnamAdmin}
            activeSection={activeSection}
            mobileNav={mobileNav}
          />
        </header>
        <LoadingBar id="react-loading-bar" />
      </AngularModalDimmer>
    </LayoutHeader>
  );
};

// needed for angulars loading states
const LoadingBar = styled.div`
  position: fixed;
  height: 1px;
  background: ${Colors.brand};

  width: 0%;
  opacity: 1;
`;
